/**
 * 날짜 입력 — 텍스트 입력 우선 + 캘린더 픽커는 [📅] 버튼으로 선택적.
 *
 * 기존 `<input type="date">` 의 native 캘린더가 매번 떠서 클릭 입력만 가능했던 UX 를
 * 숫자 키보드 입력 위주로 바꾼다. 입력은 다음 형태 모두 허용:
 *   - 8 자리 숫자: 20260315  → '2026-03-15' 자동 포맷
 *   - YYYY-MM-DD: 2026-03-15 그대로
 *   - YYYY.MM.DD / YYYY/MM/DD 등 구분자 혼합 — 숫자만 추출 후 포맷
 *
 * `value` 는 항상 'YYYY-MM-DD' 또는 '' (빈 값). 부모는 기존 type="date" 와 동일하게 사용 가능.
 * 입력 중 형식 미완성 (예: '2026-03') 일 때 부모에 commit 하지 않고 로컬에서만 보관.
 * blur 시 형식이 유효하지 않으면 마지막으로 유효했던 값으로 되돌림.
 *
 *   <DateInput value={form.contractDate} onChange={(v) => setForm({...form, contractDate: v})} />
 *
 * type="date" 를 그대로 쓰던 기존 prop (required / disabled / className 등) 도 받음.
 */

import { useEffect, useRef, useState } from 'react';

export interface DateInputProps {
  value: string;
  onChange: (next: string) => void;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  min?: string;
  max?: string;
  placeholder?: string;
  /** 입력 박스에 직접 적용할 추가 className (래퍼 외) */
  inputClassName?: string;
  id?: string;
  name?: string;
  /** picker 버튼 hidden — 일부 위치에서 공간 절약용 */
  hidePicker?: boolean;
  /** blur 시 부모 onChange 추가로 호출 (예: 폼 자동저장) */
  onBlur?: () => void;
  /**
   * **년-월까지만**(`2026-10`) 적는 것도 받아 준다 (v2.172.0 · 납품계·착수계 요청).
   *
   * ⭐**보태는 기능이다** — 끄면(기본) 종전과 한 글자도 다르지 않고, 켜도 **비우기**와
   *   **년-월-일 전부 적기**는 그대로 된다. 년-월만 적는 것은 **선택**이다.
   *   (사용자 요청 2026-09-14: 「기존처럼 아주 비우거나 년월 다 쓰는건 기본으로 가능해야 하고」)
   *
   * 켜면 `value`·`onChange` 가 `''` · `YYYY-MM` · `YYYY-MM-DD` **세 가지**를 오간다 —
   * 받는 쪽(서버·폼)이 그 셋을 다 다룰 수 있어야 한다.
   */
  allowMonthOnly?: boolean;
}

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 입력을 'YYYY-MM-DD' 로 정규화.
 *  - '' → '' (빈 값 OK)
 *  - 8자리 숫자 (구분자 없음): YYYY+MM+DD
 *  - 구분자 (- . /) + 4자리 연도: YYYY{sep}M{sep}D — 월/일 1~2 자리, zero-padding
 *  - 구분자 + 연도 생략 (월/일만): M{sep}D → 현재 연도 자동 보충
 *  - 그 외 (진행중 입력 / 잘못된 형식): null (커밋 보류)
 *  - 논리 무효 날짜 (예: 2월 30일): null
 */
/**
 * 년-월까지만 적었나 — `2026-10` · `2026.10` · `202610` 을 `YYYY-MM` 으로.
 * ⛔**8자리(20261031)는 여기서 잡지 않는다** — 그건 날짜다. 6자리만 본다.
 * ⛔`10` 처럼 달만 적은 것도 받지 않는다 — 날짜 입력의 「5-15」 같은 진행 중 입력과 구별되지 않는다.
 */
function normalizeMonthOnly(raw: string): string | null {
  const t = raw.trim();
  const m = t.match(/^(\d{4})[-./](\d{1,2})$/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    if (mo < 1 || mo > 12 || y < 1900 || y > 2999) return null;
    return `${m[1]}-${String(mo).padStart(2, '0')}`;
  }
  const digits = t.replace(/\D/g, '');
  if (t === digits && digits.length === 6) {
    const y = Number(digits.slice(0, 4));
    const mo = Number(digits.slice(4, 6));
    if (mo < 1 || mo > 12 || y < 1900 || y > 2999) return null;
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}`;
  }
  return null;
}

function normalize(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return '';

  // 4자리 연도 포함 — YYYY-M-D / YYYY.MM.DD / YYYY/M/D
  const full = trimmed.match(/^(\d{4})[-./](\d{1,2})[-./](\d{1,2})$/);
  if (full) {
    const y = full[1]!;
    const mi = Number(full[2]);
    const di = Number(full[3]);
    if (mi < 1 || mi > 12 || di < 1 || di > 31) return null;
    if (!isValidDate(Number(y), mi, di)) return null;
    return `${y}-${String(mi).padStart(2, '0')}-${String(di).padStart(2, '0')}`;
  }

  // 연도 생략 — 월/일만 (예: 2-5, 02.05, 12/31) → 현재 연도 자동
  const short = trimmed.match(/^(\d{1,2})[-./](\d{1,2})$/);
  if (short) {
    const mi = Number(short[1]);
    const di = Number(short[2]);
    if (mi < 1 || mi > 12 || di < 1 || di > 31) return null;
    const y = new Date().getFullYear();
    if (!isValidDate(y, mi, di)) return null;
    return `${y}-${String(mi).padStart(2, '0')}-${String(di).padStart(2, '0')}`;
  }

  // 8자리 숫자 only
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length !== 8) return null;
  const y = digits.slice(0, 4);
  const m = digits.slice(4, 6);
  const d = digits.slice(6, 8);
  const mi = Number(m);
  const di = Number(d);
  if (mi < 1 || mi > 12 || di < 1 || di > 31) return null;
  if (!isValidDate(Number(y), mi, di)) return null;
  return `${y}-${m}-${d}`;
}

/** 논리 검증 — Date 가 입력 값을 그대로 갖는지 확인 (예: 2/30 → 3/2 로 wrap → false). */
function isValidDate(y: number, m: number, d: number): boolean {
  const date = new Date(y, m - 1, d);
  return (
    date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d
  );
}

export function DateInput({
  value,
  onChange,
  className,
  required,
  disabled,
  min,
  max,
  placeholder = 'YYYY-MM-DD',
  inputClassName,
  id,
  name,
  hidePicker,
  onBlur,
  allowMonthOnly = false,
}: DateInputProps) {
  /** 년-월도 받는가에 따라 달라지는 정규화 — 날짜가 먼저, 안 되면 년-월 */
  const norm = (raw: string): string | null => {
    const asDate = normalize(raw);
    if (asDate !== null) return asDate;
    return allowMonthOnly ? normalizeMonthOnly(raw) : null;
  };
  // 입력 중 임시 텍스트 (커밋 전). 부모 value 와 sync.
  const [text, setText] = useState<string>(value || '');
  // 부모 value 변경 시 동기화 (외부에서 form reset 등)
  useEffect(() => {
    setText(value || '');
  }, [value]);

  const pickerRef = useRef<HTMLInputElement>(null);

  function commit(raw: string) {
    setText(raw);
    // 입력 중 자동 커밋은 사용자가 "풀 형식" 입력을 마쳤다고 명확한 경우만:
    //   - 8자리 숫자 (구분자 없음): "20260515"
    //   - YYYY-MM-DD 형식 10자: "2026-05-15"
    // 그 외 (예: "5-15", "2-5", "2026-1-1") 는 부모에 커밋 안 함 → blur/Enter
    // 때까지 보류. 사용자가 "5-1" 까지만 친 시점에 5/1 로 자동 변환되어 "5-15"
    // 입력이 막히던 문제 방지.
    const digits = raw.replace(/\D/g, '');
    // ⚠년-월은 **자동 커밋하지 않는다** — 「2026-1」 을 치는 도중에 「2026-01」 로 굳어 버리면
    //   그 뒤 「0-31」 을 이어 칠 수가 없다. 년-월은 blur·Enter 에서만 확정한다.
    const autoCommit = digits.length === 8 || (raw.length === 10 && ISO_RE.test(raw));
    if (!autoCommit) return;
    const normalized = normalize(raw);
    if (normalized === null) return;
    if (normalized !== value) onChange(normalized);
  }

  // 정규화 + 부모 커밋. blur 와 Enter 둘 다 사용.
  function commitNormalized() {
    const normalized = norm(text);
    if (normalized === null) {
      // 무효 입력 → 이전 부모 값으로 복원
      setText(value || '');
      return;
    }
    // 표시 텍스트는 항상 정규화된 형태로 (예: '2-5' → '2026-02-05')
    if (normalized !== text) setText(normalized);
    if (normalized !== value) onChange(normalized);
  }

  function handleBlur() {
    commitNormalized();
    onBlur?.();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // Enter = blur 와 동일하게 정규화 적용. 부모 form 의 submit 트리거 차단.
    // 한글 IME 조합 중인 Enter 는 무시.
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      commitNormalized();
    }
  }

  function openPicker() {
    const el = pickerRef.current;
    if (!el) return;
    // Chrome 99+ / Edge / Firefox 101+: showPicker 지원. 미지원 브라우저는
    // focus 만으로도 native 입력 도움 (캘린더 키보드 단축키 등) 가능.
    try {
      el.showPicker();
    } catch {
      el.focus();
    }
  }

  // 외부에서 보여지는 텍스트 — 진행중 입력 그대로 보이도록 text 사용,
  // 단 value 와 동기화된 후엔 YYYY-MM-DD 포맷.
  const displayText = text;

  // hidden picker 의 value 는 항상 부모 ISO. picker 선택 시 onChange 즉시 커밋.
  // wrapper 는 flex — 부모 div 가 block 이면 자연스럽게 width 100% 채움.
  // 단일 라인 inline 사용처는 className 으로 'inline-flex' 전달 가능.
  // relative 필수: 아래 hidden date input(showPicker 앵커)의 offsetParent 를 이
  //   래퍼로 고정해야 캘린더가 실제 필드 위치에 뜬다. relative 가 없으면
  //   offsetParent 가 <body> 가 되어(0×0 앵커) 캘린더가 엉뚱한 위치·잘려서 뜬다.
  return (
    <span className={`relative flex items-center gap-1 ${className ?? ''}`}>
      <input
        id={id}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={displayText}
        onChange={(e) => commit(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={allowMonthOnly && placeholder === 'YYYY-MM-DD' ? 'YYYY-MM-DD 또는 YYYY-MM' : placeholder}
        required={required}
        disabled={disabled}
        maxLength={10}
        className={`${
          inputClassName ??
          'border border-slate-300 rounded px-2 py-1 text-sm outline-none focus:border-blue-500'
        } flex-1 min-w-0`}
      />
      {!hidePicker && (
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled}
          tabIndex={-1}
          title="달력에서 선택"
          aria-label="달력 열기"
          className="btn btn-secondary btn-sm"
        >
          📅
        </button>
      )}
      <input
        ref={pickerRef}
        type="date"
        value={ISO_RE.test(value) ? value : ''}
        onChange={(e) => {
          const v = e.target.value;
          setText(v);
          if (v !== value) onChange(v);
        }}
        min={min}
        max={max}
        disabled={disabled}
        // 화면에서 숨김 (picker 만 트리거하는 hidden input).
        // display:none 으로 두면 showPicker() 가 InvalidStateError 던질 수 있어
        // 시각만 숨기고 opacity 0 으로. 단, 0×0 이면 브라우저가 캘린더를 앵커
        // 위치(=필드 아래/위)로 못 띄우고 잘려서 뜨므로, 래퍼(relative) 전체를
        // 덮도록 inset:0 으로 실제 크기를 준다. pointer-events:none 이라 그 아래
        // 텍스트 입력/버튼 클릭은 그대로 통과한다.
        tabIndex={-1}
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          pointerEvents: 'none',
        }}
      />
    </span>
  );
}
